import React from 'react';
import { Image } from 'js/components/common/Image';
import { compose, omit, path, identity } from 'ramda';
import { useGameConfigurationSelector } from 'js/store/hooks';
import { useLiveGameConfig, useGameImageUrls } from 'js/hooks/games';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { branch, mapProps, pure, renderNothing, withHandlers, withProps } from 'recompose';

import { FavoriteIcon } from './FavoriteIcon';
import withGameLaunch from './withGameLaunch';
import { withGameInfo } from './withGameInfo';

import style from './Game.scss';

export const GameBare = ({
    t,
    code,
    name,
    size = 'extraSmall',
    showTitle = false,
    showFeatureIcons = true,
    gameFeed,
    showFavoriteIcon,
    showRecentlyPlayedIcon,
    className = '',
    setGameRef,
    setImageRef,
    setGameImageRef,
    onClick,
    onMouseMove,
    isGameNew,
    isGameHot,
    isGamePopular,
    isGameExclusive,
    tabIndex,
    liveLaunchAlias,
    includeLiveGames = false,
    displayMode = ''
}) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = includeLiveGames && liveGameConfig?.launchAlias;
    const { dynamicImageUrl, limits, isScheduled, openingDateTime, dealerName, language, seatsInfo } = liveGameConfig || {};
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });

    return (
        <AccessibilityElement
            onClick={onClick}
            tabIndex={tabIndex}
            data-game-code={code}
            elementRef={setGameRef}
            className={`${style.container} ${style[size]} ${style[displayMode] || ''} ${className}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
        >
            <div
                aria-hidden={true}
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={style.imageContainer}
            >
                <Image
                    alt={name}
                    src={imageUrls}
                    ref={setImageRef}
                    className={style.image}
                />
                {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
                {(showFeatureIcons || showFavoriteIcon) && <div className={style.background} />}
                {showRecentlyPlayedIcon && <div className={style.recentlyPlayedIcon} />}
            </div>
            <div className={`${style.sideLabels} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                {showFeatureIcons && (
                    <div className={style.labels}>
                        {isGameNew && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.new')}</span>}
                        {isGamePopular && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>}
                        {isGameHot && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.hot')}</span>}
                        {isGameExclusive && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>}
                    </div>
                )}
                {isLiveGame && limits && <div className={style.liveLimits}>{limits}</div>}
            </div>
            {isScheduledLiveGame && (
                <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {openingDateTime
                        ? (
                            <>
                                <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                <span>{openingDateTime}</span>
                            </>
                        )
                        : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                    }
                </div>
            )}
            <div className={`${style.sideIcons} ${showTitle ? style.withTitle : ''}`}>
                {isLiveGame && (
                    <div className={style.liveInfo} aria-hidden={true}>
                        <span>{dealerName}</span>
                        <span>{language}</span>
                    </div>
                )}
                {!showTitle && showFavoriteIcon && !isLiveGame && (
                    <FavoriteIcon
                        code={code}
                        gameFeed={gameFeed}
                        className={style.favorite}
                    />
                )}
                {isLiveGame && seatsInfo && (
                    <div className={style.seatsInfo} aria-hidden={true}>
                        <span>{seatsInfo}</span>
                        <span className={style.seatsIcon} />
                    </div>
                )}
            </div>
            {showTitle && (
                <div className={style.titleWrapper}>
                    <span className={`${style.name} ${style.ellipsis}`} aria-hidden={true}>{name}</span>
                    {showFavoriteIcon && (
                        <FavoriteIcon
                            code={code}
                            isFeatured={true}
                            gameFeed={gameFeed}
                            className={style.favorite}
                        />
                    )}
                </div>
            )}
        </AccessibilityElement>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useGameConfigurationSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    branch(
        ({ showGameInfo }) => showGameInfo,
        withGameInfo,
        withGameLaunch
    ),
    withHandlers(({
        launchGame, openGameInfo, disableGameLaunch,
        // for game launch tracking
        container, gameFeed, tidPgg, categoryName, pggCategoryName, categoryPosition, gamePositionInCategory
    }) => ({
        onClick: () => (event) => {
            const onGameClick = disableGameLaunch ? identity : launchGame || openGameInfo;

            event.preventDefault();
            onGameClick({ container, gameFeed, tidPgg, categoryName, pggCategoryName, categoryPosition, gamePositionInCategory });
        }
    })),
    withProps(({ code }) => ({
        ...useGameConfigurationSelector(path(['games', code]))
    })),
    mapProps(omit(['description', 'demoModeSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
